import Title from "@/components/common/Title";
import type { UserMetadata } from "@supabase/supabase-js";
import { CatchBoundary } from "@tanstack/react-router";
import { capitaliseSentence } from "../../../services/capitaliseSentence";
import { useUserMetadataQuery } from "../api/selectUserMetadata";

interface TitleContent {
  title: string;
  description?: string;
}

function getTitleContent(userMetadata: any): TitleContent {
  if (!userMetadata) return { title: "Sign Up" };

  const organisationName = userMetadata?.organisation_name;
  const injectingName = userMetadata?.injecting_clinician_name;
  const referringName = userMetadata?.referring_clinician_name;

  if (!organisationName) {
    return { title: "Sign Up" };
  }

  if (userMetadata.quickstart) {
    return {
      title: `Sign up (${userMetadata.quickstart.quickstart_name})`,
      description: userMetadata.quickstart.quickstart_description ?? "",
    };
  }

  const roleText = userMetadata.role
    ? ` as ${capitaliseSentence(userMetadata.role)}`
    : "";
  const orgText = organisationName ? ` for ${organisationName}` : "";

  // Collect unique names
  const names = [injectingName, referringName]
    .filter(Boolean);
  const uniqueNames = Array.from(new Set(names));

  let namesText = "";
  if (uniqueNames.length === 1) {
    namesText = ` with ${uniqueNames[0]}`;
  } else if (uniqueNames.length > 1) {
    namesText = ` with ${uniqueNames.join(", ")}`;
  }

  return {
    title: `Sign up${roleText}${orgText}${namesText}`,
    description: "",
  };
}

function ErrorTitle() {
  return <Title words="Sign Up" />;
}

export function SignupTitle({ search }: { search: UserMetadata }) {
  const { data: titleData } = useUserMetadataQuery(search);
  const titleContent = getTitleContent(titleData);

  return (
    <CatchBoundary
      getResetKey={() => "signup-title"}
      errorComponent={ErrorTitle}
    >
      <Title
        words={titleContent.title}
        description={titleContent.description}
      />
    </CatchBoundary>
  );
}

export default SignupTitle;
